<template>
    <FormField :label="label ? label : null" class="eventwise-currency-field" @click="onClick($event)">
        <NumberInput
            v-model="amount"
            :currency="effectiveCurrency"
            :min
            :step
            :max
            :disabled
            class="eventwise-currency-input"
            :dp="2"
            :input-class="compact ? '!tw-py-1 !tw-px-2.5 !tw-pl-2.5 !tw-text-xs' : ''"
            :currency-class="compact ? '!tw-py-1 !tw-px-2 !tw-text-xs' : ''"
            @update:model-value="emitValue()"
        />
    </FormField>
</template>
<script setup lang="ts">
import FormField from '@/js/components/widgets/FormField.vue';
import NumberInput from '@/js/components/controls/NumberInput.vue';
import { useDataStore } from '@/js/stores/DataStore.js';
import { formatCurrency } from '@/js/utils.js';
import { onMounted, ref, watch, computed } from 'vue';

const props = defineProps({
    controls: {
        type: Boolean,
        default: false,
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    label: {
        type: [String],
        default: null,
    },
    preventDefault: {
        type: Boolean,
        default: true,
    },
    stopPropogation: {
        type: Boolean,
        default: true,
    },
    min: {
        type: [Number, String],
        default: null,
    },
    step: {
        type: [Number, String],
        default: 1,
    },
    max: {
        type: [Number],
        default: null,
    },
    currencyCode: {
        type: String,
        default: 'GBP',
    },
    currency: {
        type: String,
        default: '',
    },
    compact: {
        type: Boolean,
        default: false,
    },
});

const model = defineModel<string | number | null>();
const amount = ref<string | number>(0);
const dataStore = ref(useDataStore());

watch(model, (val) => {
    if (val === '-') {
        return;
    }
    if (!val) {
        return;
    }
    const a = typeof val === 'string' ? parseFloat(val) : val;
    if (a && isNaN(a)) {
        amount.value = '';
    } else {
        amount.value = a;
    }
});

onMounted(() => {
    amount.value = parseFloat(String(model.value)).toFixed(2);
    emitValue();
});
const effectiveCurrency = computed(() => {
    if (props.currency) return props.currency;

    // If not, compute it using currencyCode or fallback
    const eventCurrency = dataStore.value.get('current_event.currency_code');
    const code = props.currencyCode || eventCurrency || 'GBP';
    return formatCurrency(0, 0, code).replace('0', '');
});

const emitValue = () => {
    if (amount.value === '-') {
        return;
    }
    model.value = parseFloat(String(amount.value));
};

const onClick = (e: Event) => {
    if (props.stopPropogation) {
        e.stopPropagation();
    }
    if (props.preventDefault) {
        e.preventDefault();
    }
};
</script>
<style scoped>
.eventwise-currency-input {
    input {
        text-align: right;
    }
    &:not(.is-expanded) {
        input {
            max-width: 125px;
        }
    }
}
</style>
